import { z } from 'zod';

import i18n, { supportedLanguages } from '~/utils/i18n';

/**
 * A backend-authored text, keyed by language.
 *
 * Labels and help texts arrive as language maps rather than translation keys,
 * because a new template ships as a backend change and its texts would have no
 * entry in this app's catalogs.
 */
export const localizedTextSchema = z.record(z.string(), z.string());

const TEMPLATE_FIELD_TYPES = [
  'string',
  'url',
  'enum',
  'company',
  'uuid',
] as const;

/**
 * A field type this app knows how to render.
 *
 * Unknown types fall back to `string` rather than failing the response: a
 * backend that ships a new type would otherwise blank the whole picker, which
 * is the opposite of a new system being a backend-only change. Both the input
 * and the validation degrade to a plain text field.
 */
export const templateFieldTypeSchema = z
  .enum(TEMPLATE_FIELD_TYPES)
  .catch('string');

export const dataSyncTemplateFieldSchema = z.object({
  advanced: z.boolean(),
  default: z.unknown(),
  help: localizedTextSchema,
  key: z.string().min(1),
  label: localizedTextSchema,
  options: z.array(z.string()),
  required: z.boolean(),
  secret: z.boolean(),
  type: templateFieldTypeSchema,
});

const dataSyncTemplateTaskSchema = z.object({
  defaultEnabled: z.boolean(),
  entityType: z.string().min(1),
  key: z.string().min(1),
  name: localizedTextSchema,
});

export const dataSyncTemplateSchema = z.object({
  description: localizedTextSchema,
  formSchema: z.array(dataSyncTemplateFieldSchema),
  iconSvg: z.string().nullable(),
  label: localizedTextSchema,
  tasks: z.array(dataSyncTemplateTaskSchema),
  uid: z.string().min(1),
  version: z.number().int(),
});

export const dataSyncTemplateListSchema = z.object({
  data: z.array(dataSyncTemplateSchema),
});

/**
 * Creating a job from a template. The params are keyed by the template's own
 * field keys, so they are sent verbatim rather than case-converted.
 */
export const createJobFromTemplateSchema = z.object({
  language: z.enum(supportedLanguages),
  name: z.string().trim().min(1),
  params: z.record(z.string(), z.string()),
  taskKeys: z.array(z.string().min(1)).min(1),
  templateUid: z.string().min(1),
});

/**
 * The validation for one template field, derived from what the template says
 * the field holds.
 *
 * The rules carry their own localized messages, because what the app's shared
 * error map produces for a format issue ("Ungültig: URL") is thinner than what
 * an operator filling in a connection needs. The backend validates all of this
 * again and answers 422 with a reason; this exists so the operator is told
 * before they submit, not as the guard.
 *
 * Every optional branch is `.nullable()`: clearing a select writes null into
 * the form, null and '' both read as "unanswered" and are dropped by the
 * submit, and the ZodNullable wrapper is also what tells `isFieldRequired` not
 * to mark the field with a required asterisk.
 */
const constrainedFieldSchema = (
  field: z.infer<typeof dataSyncTemplateFieldSchema>,
): z.ZodType<string, string> | null => {
  if (field.type === 'url') {
    return z.url({
      error: i18n.t(($) => $.template.validation.invalidUrl, {
        ns: 'dataSync',
      }),
    });
  }

  if (field.type === 'company' || field.type === 'uuid') {
    // Not the shared ValidUuidOrNotSetSchema: its nullish also admits
    // undefined, which is never a form value here.
    return z.uuid({
      error: i18n.t(($) => $.template.validation.invalidId, { ns: 'dataSync' }),
    });
  }

  if (field.type === 'enum' && field.options.length > 0) {
    return z.enum(field.options as [string, ...string[]], {
      error: i18n.t(($) => $.template.validation.invalidOption, {
        ns: 'dataSync',
      }),
    });
  }

  // An enum the template left without options has nothing to constrain against
  // and is handled as free text, same as a plain string field.
  return null;
};

const fieldSchema = (
  field: z.infer<typeof dataSyncTemplateFieldSchema>,
): z.ZodType<string | null, string | null> => {
  const constrained = constrainedFieldSchema(field);

  if (constrained === null) {
    // Trimmed, because the submit drops values that are only whitespace:
    // without this the form would call a blank required field valid and the
    // request would then go out without it.
    return field.required
      ? z
          .string()
          .trim()
          .min(
            1,
            i18n.t(($) => $.template.validation.required, { ns: 'dataSync' }),
          )
      : z.string().trim().nullable();
  }

  return field.required
    ? constrained
    : z.union([z.literal(''), constrained]).nullable();
};

/**
 * The whole setup form for one template: a job name, the template's own inputs,
 * and at least one task to create.
 *
 * Built per template rather than written out, because which fields exist is
 * only known once the backend has described them.
 */
export const templateSetupSchema = (
  template: z.infer<typeof dataSyncTemplateSchema>,
) =>
  z.object({
    name: z.string().trim().min(1),
    params: z.object(
      Object.fromEntries(
        template.formSchema.map((field) => [field.key, fieldSchema(field)]),
      ),
    ),
    taskKeys: z.array(z.string().min(1)).min(1),
  });
